import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Collapse, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  ipdSemanticSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { certifyDeath, getAssignable, recordBodyHandling } from "../api/emergencyApi";

/**
 * Death registration (emergency-death-registration, ticket 01).
 *
 * The death record is opened when a visit closes as "expired"; this card shows
 * it and lets the Emergency Provider certify it — certification is what makes
 * the record valid. Certifying is a deliberate action on a closed visit, so the
 * card stays actionable even though the rest of the visit is read-only.
 *
 * The certifying doctor defaults to the visit's Emergency Provider (one tap);
 * a brought-in-dead arrival that never had a provider picks the doctor who
 * pronounced death.
 */

/** ISO → value a datetime-local input accepts (local time). */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DeathRegistrationCard({ visit, onCertified }) {
  const record = visit.death_record;

  const [editing, setEditing] = useState(false);
  const [doctors, setDoctors] = useState(null);
  const [doctorId, setDoctorId] = useState(visit.provider_id || "");
  const [deathAt, setDeathAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [bodyTarget, setBodyTarget] = useState("");
  const [bodyOverride, setBodyOverride] = useState("");
  const [bodySaving, setBodySaving] = useState(false);

  const loadDoctors = useCallback(async () => {
    try {
      const data = await getAssignable();
      setDoctors(data?.doctors || []);
    } catch (e) {
      // Leave the null-guarded skeleton only while genuinely loading; on error
      // fall to an empty list so the select renders (never with a preselected
      // doctor before its options exist), and the alert explains.
      setDoctors([]);
      setError(e?.message || "Could not load the doctors");
    }
  }, []);

  useEffect(() => {
    if (!editing) return;
    setDoctorId(visit.provider_id || "");
    setDeathAt(toLocalInput(record?.death_datetime));
    setError("");
    loadDoctors();
  }, [editing, visit.provider_id, record, loadDoctors]);

  if (!record) return null;

  const submit = async () => {
    if (!doctorId) return;
    setSaving(true);
    setError("");
    try {
      await certifyDeath(visit.id, {
        certifyingDoctorId: doctorId,
        deathDatetime: deathAt ? new Date(deathAt).toISOString() : undefined,
      });
      await onCertified?.();
      setEditing(false);
    } catch (e) {
      setError(e?.message || "Could not certify the death");
    } finally {
      setSaving(false);
    }
  };

  const incomplete = (record.incomplete || []).length > 0;

  const saveBody = async () => {
    if (!bodyTarget || (incomplete && !bodyOverride.trim())) return;
    setBodySaving(true);
    setError("");
    try {
      await recordBodyHandling(visit.id, {
        bodyReleasedTo: bodyTarget,
        overrideReason: incomplete ? bodyOverride.trim() : undefined,
      });
      await onCertified?.();
    } catch (e) {
      setError(e?.message || "Could not record the body handling");
    } finally {
      setBodySaving(false);
    }
  };

  return (
    <IpdSectionCard
      title="Death registration"
      subtitle="Certification by the Emergency Provider is what makes the record valid"
      actions={
        !record.certified && !editing ? (
          <IpdButton
            size="small"
            startIcon={<AssignmentTurnedInIcon />}
            onClick={() => setEditing(true)}
          >
            Certify death
          </IpdButton>
        ) : null
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>Time of death</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {record.death_datetime ? formatDateTime(record.death_datetime) : "—"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>Certification</Typography>
          {record.certified ? (
            <Box
              component="span"
              sx={{
                ...ipdSemanticSx("success"),
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.9,
                py: 0.2,
                borderRadius: `${IPD_SHAPE.radiusSm}px`,
                fontSize: 11.5,
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 13 }} />
              Certified
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                ...ipdSemanticSx("warning"),
                px: 0.9,
                py: 0.2,
                borderRadius: `${IPD_SHAPE.radiusSm}px`,
                fontSize: 11.5,
              }}
            >
              Awaiting certification
            </Box>
          )}
        </Box>
        {record.certified && (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted, textAlign: "right" }}>
            {record.certified_by_name ? `Dr. ${record.certified_by_name}` : "Certified"}
            {record.certified_at ? ` · ${formatDateTime(record.certified_at)}` : ""}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>Body released to</Typography>
          {record.body_released_to ? (
            <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>
              {record.body_released_to}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textMuted }}>Not recorded</Typography>
          )}
        </Box>
      </Box>

      {/* A medico-legal death's certificate is withheld and its body is
          evidence — the family option is refused (ticket 03). */}
      {record.certificate_withheld && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          Death certificate withheld — pending post-mortem (medico-legal case). The body goes
          to the mortuary or police, not the family.
        </Alert>
      )}

      {!record.body_released_to && (
        <Box sx={{ mt: 1.5, display: "grid", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 190 }}>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Record body released to</Typography>
              <TextField
                select
                value={bodyTarget}
                onChange={(e) => setBodyTarget(e.target.value)}
                size="small"
                fullWidth
                sx={ipdElevatedInputSx}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                inputProps={{ "aria-label": "Body released to" }}
              >
                <MenuItem value="" disabled>Where the body went</MenuItem>
                <MenuItem value="mortuary">Mortuary</MenuItem>
                <MenuItem value="police">Police</MenuItem>
                <MenuItem value="family" disabled={record.certificate_withheld}>
                  Family{record.certificate_withheld ? " — not permitted (MLC)" : ""}
                </MenuItem>
              </TextField>
            </Box>
            <IpdButton
              size="small"
              onClick={saveBody}
              loading={bodySaving}
              disabled={!bodyTarget || (incomplete && !bodyOverride.trim())}
            >
              Record
            </IpdButton>
          </Box>
          {incomplete && (
            <Box>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text, mb: 0.5 }}>
                Record incomplete ({record.incomplete.join(", ")}). Give a reason to release the body anyway.
              </Typography>
              <TextField
                value={bodyOverride}
                onChange={(e) => setBodyOverride(e.target.value)}
                size="small"
                fullWidth
                placeholder="Reason to release on an incomplete record"
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Override reason to release the body" }}
              />
            </Box>
          )}
        </Box>
      )}

      <Collapse in={editing} unmountOnExit>
        <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
          <Box>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
              Certifying doctor <span style={{ color: IPD_COLORS.error.text }}>*</span>
            </Typography>
            {/* Hold the select until the option list exists, so a preselected
                Emergency Provider never renders as an out-of-range value. */}
            {doctors === null ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <TextField
                select
                value={doctorId}
                onChange={(e) => setDoctorId(e.target.value)}
                size="small"
                fullWidth
                sx={ipdElevatedInputSx}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                inputProps={{ "aria-label": "Certifying doctor" }}
                helperText={
                  visit.provider_name
                    ? "Defaults to the Emergency Provider."
                    : "This visit had no Emergency Provider — name the doctor who pronounced death."
                }
              >
                <MenuItem value="" disabled>
                  {doctors.length ? "Name the certifying doctor" : "No doctors at this facility"}
                </MenuItem>
                {doctors.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}{d.specialization ? ` — ${d.specialization}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
          <Box sx={{ maxWidth: 260 }}>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Time of death</Typography>
            <TextField
              type="datetime-local"
              value={deathAt}
              onChange={(e) => setDeathAt(e.target.value)}
              size="small"
              fullWidth
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": "Time of death" }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <IpdButton variant="outlined" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </IpdButton>
            <IpdButton onClick={submit} loading={saving} disabled={!doctorId}>
              Certify death
            </IpdButton>
          </Box>
        </Box>
      </Collapse>
    </IpdSectionCard>
  );
}
